import { Link } from "react-router-dom";
import { AlertCircle, CheckCircle2, IdCard, X } from "lucide-react";
import { formatKycRejectionReason, isKycExpiredReason, kycMissingItems, kycRetryLabel } from "../lib/kyc";

export default function KycRequiredDialog({ context = "continue", onClose, user }) {
  const missing = kycMissingItems(user);
  const identityRequired = user?.identity_verification_required !== false;
  const status = String(user?.identity_verification_status || "not_submitted").toLowerCase();
  const rejected = identityRequired && status === "rejected";
  const pending = identityRequired && status === "pending";
  const expired = rejected && isKycExpiredReason(user.identity_rejection_reason);
  if (!user || user === false || missing.length === 0) return null;

  return (
    <div className="fixed inset-0 z-[130] grid place-items-center bg-slate-950/70 p-4 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-2xl border border-[#2F6FA5]/30 bg-white p-5 text-slate-950 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#2F6FA5] text-white">
              <IdCard className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#2F6FA5]">
                {identityRequired ? "KYC required" : "Phone required"}
              </p>
              <h2 className="mt-1 text-xl font-black">
                {identityRequired ? "Verify before withdrawal" : "Verify phone before withdrawal"}
              </h2>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 text-slate-500 hover:bg-slate-100">
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="mt-4 text-sm leading-6 text-slate-600">
          {rejected
            ? expired
              ? `Your last Didit session expired before it was finished. Start KYC again from the dashboard before you ${context}.`
              : `Your last ID verification was rejected. Review the reason below, then resubmit from the dashboard before you ${context}.`
            : pending
              ? `Your ID verification is still pending. Continue the Didit check from the dashboard before you ${context}.`
              : identityRequired
                ? `You can play and load without ID verification. Before you ${context}, complete secure Didit ID verification.`
                : `ID verification is currently turned off. Before you ${context}, verify your phone number from the dashboard.`}
        </p>

        {rejected && (
          <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-rose-700">
              <AlertCircle className="h-4 w-4" />
              {expired ? "Session expired" : "Why verification was rejected"}
            </div>
            <p className="mt-2 text-sm font-semibold leading-6 text-rose-900">
              {formatKycRejectionReason(user.identity_rejection_reason)}
            </p>
            <p className="mt-2 text-xs text-slate-600">
              {expired ? "This is not permanent. Start KYC again and complete all Didit steps." : "Fix this issue and submit Didit verification again."}
            </p>
          </div>
        )}

        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
          <div className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">
            {rejected ? "Still needed" : "Needed now"}
          </div>
          <ul className="mt-2 space-y-2 text-sm font-semibold text-slate-800">
            {missing.map((item) => (
              <li key={item} className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#2F6FA5]" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          <Link
            to="/dashboard"
            onClick={onClose}
            className="inline-flex flex-1 items-center justify-center rounded-full bg-[#2F6FA5] px-5 py-3 text-sm font-bold text-white hover:bg-[#4E91C8]"
          >
            {rejected ? kycRetryLabel(user.identity_rejection_reason) : pending ? "Continue KYC" : identityRequired ? "Complete KYC" : "Verify phone"}
          </Link>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex flex-1 items-center justify-center rounded-full border border-slate-200 px-5 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50"
          >
            Later
          </button>
        </div>
      </div>
    </div>
  );
}
